/**
 * Network Status Panel for Settings Dialog
 * Displays network status for all exchanges with clickable rows for details
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Tooltip,
  Chip,
} from '@mui/material';
import { useAppSelector } from '../../app/store';
import { ALL_EXCHANGE_INDICES } from '../../types/networkStatus';
import { EXCHANGES } from '../../types/opportunity';
import { NetworkStatusDetailsDialog } from './NetworkStatusDetailsDialog';

/**
 * Format time elapsed since timestamp
 */
function formatTimeAgo(timestamp: number): string {
  if (!timestamp) return 'Never';
  
  const now = Date.now();
  const diff = now - timestamp;
  
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 0) return `${days} day${days !== 1 ? 's' : ''} ago`;
  if (hours > 0) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  if (minutes > 0) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  if (seconds > 0) return `${seconds} second${seconds !== 1 ? 's' : ''} ago`;
  return 'Just now';
}

/**
 * Format exchange name for display
 */
function formatExchangeName(exchange: number): string {
  return EXCHANGES[exchange] || 'Unknown';
}

export const NetworkStatusPanel: React.FC = () => {
  const { t } = useTranslation();
  const [selectedExchange, setSelectedExchange] = React.useState<number | null>(null);
  const [detailsOpen, setDetailsOpen] = React.useState(false);

  // Get all network status data at once
  const allNetworkStatus = useAppSelector(state => state.networkStatus.data);
  const allLastFetchTime = useAppSelector(state => state.networkStatus.lastFetchTime);
  
  // Calculate stale status for all exchanges
  const exchangeStatuses = ALL_EXCHANGE_INDICES.map(exchange => {
    const networkStatus = allNetworkStatus[exchange];
    const lastFetchTime = allLastFetchTime[exchange] || 0;
    const isStale = lastFetchTime ? (Date.now() - lastFetchTime) > (60 * 60 * 1000) : true; // 1 hour

    // Just get the crypto count if data exists
    let cryptoCount = 0;
    if (networkStatus && networkStatus.cryptos && Array.isArray(networkStatus.cryptos)) {
      cryptoCount = networkStatus.cryptos.length;
    }

    return {
      exchange,
      networkStatus,
      lastFetchTime,
      isStale,
      cryptoCount,
    };
  });

  const handleRowClick = (exchange: number) => {
    setSelectedExchange(exchange);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedExchange(null);
  };

  const selectedNetworkStatus = selectedExchange !== null 
    ? allNetworkStatus[selectedExchange]
    : null;

  return (
    <Box sx={{ width: '100%' }}>
      {/* Instruction */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('settings.network.clickForDetails')}
      </Typography>

      {/* Status Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('settings.exchanges.exchange')}</TableCell>
              <TableCell align="center">{t('settings.network.status')}</TableCell>
              <TableCell align="center">{t('settings.network.cryptos')}</TableCell>
              <TableCell>{t('settings.network.lastUpdated')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {exchangeStatuses.map(status => (
              <TableRow 
                key={status.exchange}
                hover
                onClick={() => handleRowClick(status.exchange)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {formatExchangeName(status.exchange)}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  {status.networkStatus ? (
                    <Tooltip title={t('settings.network.dataAvailable')}>
                      <span style={{ color: '#4caf50' }}>✅</span>
                    </Tooltip>
                  ) : (
                    <Tooltip title={t('settings.network.noData')}>
                      <span style={{ color: '#ff9800' }}>⚠️</span>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell align="center">
                  {status.cryptoCount > 0 ? (
                    <Chip
                      label={status.cryptoCount}
                      size="small"
                      variant="outlined"
                    />
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {status.isStale && (
                      <Tooltip title={t('settings.network.dataStale')}>
                        <span style={{ color: '#ff9800' }}>⚠️</span>
                      </Tooltip>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {formatTimeAgo(status.lastFetchTime)}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Information */}
      <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <span style={{ color: '#4caf50' }}>✅</span>
          <Typography variant="caption">Data Available</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <span style={{ color: '#ff9800' }}>⚠️</span>
          <Typography variant="caption">No Data / Stale</Typography>
        </Box>
      </Box>

      {/* Network Status Details Dialog */}
      {selectedExchange !== null && (
        <NetworkStatusDetailsDialog
          open={detailsOpen}
          onClose={handleCloseDetails}
          exchange={selectedExchange}
          networkStatus={selectedNetworkStatus}
        />
      )}
    </Box>
  );
};